import { Skeleton } from "antd-mobile";
import { useTranslation } from "react-i18next";
import { TokenBadge, TokenIconSize, IconArrowDown } from "~/components";
import { TokenBase } from "~/types";
import { formatNumber, formatPrice } from "~/utils/number-format";

type QuickBuySwapHeaderProps = {
    fromTokenBase: TokenBase;
    fromAmount: string;
    fromValue: string;
    toTokenBase: TokenBase;
    toAmount?: string;
    toValue?: string;
    isLoading: boolean;
};

const QuickBuySwapHeader: React.FC<QuickBuySwapHeaderProps> = ({
    fromTokenBase,
    fromAmount,
    fromValue,
    toTokenBase,
    toAmount = "0",
    toValue = "0",
    isLoading,
}) => {
    const { t } = useTranslation();

    return (
        <div className="px-4">
            <div className="w-full bg-bg_0 py-[18px] rounded-[12px]">
                <div className="flex items-center px-[12px]">
                    <div>
                        <TokenBadge
                            size={TokenIconSize.small}
                            tailIconSize={TokenIconSize.mini21}
                            tailIconClass="!border-[1.5px]"
                            icon={fromTokenBase.logo}
                            chainIcon={fromTokenBase.chain_logo}
                            symbol={fromTokenBase.symbol.toUpperCase()}
                            chainName={fromTokenBase.chain_name}
                        />
                    </div>
                    <div className="ml-[12px] flex flex-col justify-center flex-1">
                        <div className="flex justify-between text-body3 font-Regular text-text_2">
                            <div className="mt-[2px]">{t("you_pay")}</div>
                            <div className="mt-[2px]">
                                {formatPrice(fromValue)}
                            </div>
                        </div>
                        <div className="text-h4 font-Semibold text-text_1 flex items-center">
                            <span>
                                {formatNumber(fromAmount, {
                                    bigNumThreshold: 1e11,
                                })}
                            </span>
                            <span className="ml-0.5">
                                {fromTokenBase.symbol.toUpperCase()}
                            </span>
                        </div>
                    </div>
                </div>
                <div className="flex items-center pl-[12px] py-[6px]">
                    <div className="flex items-center justify-center w-[52px]">
                        <IconArrowDown className="text-text_3 text-[18px]" />
                    </div>
                    <div className="w-full h-[0.5px] bg-border_2 ml-[12px]" />
                </div>
                <div className="flex items-center px-[12px]">
                    <div className="flex-none">
                        <TokenBadge
                            size={TokenIconSize.small}
                            tailIconSize={TokenIconSize.mini21}
                            tailIconClass="!border-[1.5px]"
                            icon={toTokenBase.logo}
                            chainIcon={toTokenBase.chain_logo}
                            symbol={toTokenBase.symbol.toUpperCase()}
                            chainName={toTokenBase.chain_name}
                        />
                    </div>
                    <div className="flex-auto ml-[12px] flex flex-col justify-center overflow-hidden">
                        <div className="flex justify-between text-body3 font-Regular text-text_2">
                            <div className="mt-[2px]">{t("you_get")}</div>
                            <div className="mt-[2px]">
                                {isLoading ? (
                                    <Skeleton
                                        className="w-[70px] h-[20px] rounded"
                                        animated
                                    />
                                ) : (
                                    formatPrice(toValue)
                                )}
                            </div>
                        </div>
                        <div className="font-Semibold text-text_1 text-h4 flex items-center">
                            {isLoading ? (
                                <>
                                    <Skeleton
                                        className="w-[100px] h-[30px] rounded"
                                        animated
                                    />
                                    <Skeleton
                                        className="w-[40px] h-[30px] rounded ml-0.5"
                                        animated
                                    />
                                </>
                            ) : (
                                <>
                                    <span>
                                        {formatNumber(toAmount, {
                                            bigNumThreshold: 1e11,
                                        })}
                                    </span>
                                    <span className="ml-0.5">
                                        {toTokenBase.symbol.toUpperCase()}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default QuickBuySwapHeader;
